import { Injectable } from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { LoggerService, LogCategory, LogType, LogService } from "../core/services/logger.service";
import { of, Observable, BehaviorSubject, combineLatest } from "rxjs";
import { switchMap, tap, shareReplay, map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { ProfileService } from "../core/services/profile.service";
import { TagManagerService } from "../core/services/tag-manager.service";
import { UtilityService } from "../core/services/utility.service";


@Injectable({
    providedIn: 'root'
})
export class DegreeAuditService {

    public refreshSubject$: BehaviorSubject<boolean> = new BehaviorSubject(false); //use this to force a refetch of degree audits
    private degreeAuditApiUrl: string = environment.apiUrl + '/degreeaudit';
    public isLoadingPdf: boolean = false;


    constructor(
        private logger: LoggerService,
        private http: HttpClient,       
        private gtm: TagManagerService,
        private profile: ProfileService,
        private utility: UtilityService,
    ) {
        this.log("constructor", null, LogCategory.Constructor);
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Service, LogService.DegreeAudit, category);
    }

    // All the audits for the current user
    public myDegreeAudit$: Observable<any[]> = this.profile.viewConcentrations$
        .pipe(
            switchMap(() => this.http.get<any>(this.degreeAuditApiUrl + '/self')),
            tap((data: any[]) => {
                this.log('myDegreeAudit', 'Fetched all audit for current user', LogCategory.SubscriptionData);
            }),
            shareReplay(1)
        );

    public degreeDetail$: Observable<any> = this.myDegreeAudit$
        .pipe(
            map((audit: any) => {
            if (audit != null && audit.Audits.length > 0 && audit.Audits.find(a => a.Audits.find(b => b.IsDegree)))
                    var degree = audit.Audits.find(a => a.Audits.find(b => b.IsDegree)).Audits[0];

                return {
                    DegreeName: degree ? degree.Title : '',
                    PercentageComplete: degree ? degree.TotalPercentageComplete : 0,
                    DegreeDetail: degree ? degree.Groups : [],
                    IsManualAudit: audit.isManualAudit,
                    InProgressPercentage: degree ? degree.InProgressPercentage : 0,
                    RemainingPercentage: degree ? degree.RemainingPercentage : 0
                };                
            })
    );

    public optionalGroups$: Observable<any[]> = this.myDegreeAudit$
        .pipe(
            map((audit: any) => {
                return audit.Audits.filter( a => a.Audits.find( b => !b.IsDegree ) ) ;
            })
        )

    public optionalGroupsByType$: Observable<any[]> = combineLatest(this.optionalGroups$, this.profile.viewConcentrations$)
        .pipe(
            switchMap(([audits, concData]) => {
                return of(this.buildGroups(concData, audits, 3));
            })
        )

    public selectedByType$: Observable<any[]> = this.optionalGroupsByType$
        .pipe(
            map((groups: any) => {
                return groups.map(g =>  g.chosenGrouping);
            })
        )
     
    public top3OptionalByType$: Observable<any[]> = this.optionalGroupsByType$
        .pipe(
            map((groups: any) => {                
                return groups.map(g => g.topGrouping);
            })
        )

    public optionalByType$: Observable<any> = this.optionalGroupsByType$
        .pipe(
            map((groups: any) => {                
                return groups.map(g => g.allGrouping);                
            })
        )

    private buildGroups(concData, audits, topN): any[] {        
        var auditCollections = [];
        var groupedCollections = [];
        //Chosen concentrations
        var chosenConcs = concData.concentrations.map(c => c.Value);
        //Audits for the chosen concentrations
        var chosenConcAudits = audits.filter(a => chosenConcs.indexOf(a.Key) > -1);
        //Audits for the other concentrations not chosen, split into primary and secondary groups
        auditCollections[0] = audits.filter(a => a.Audits.find(b => b.IsPrimaryConcentration));
        auditCollections[1] = audits.filter(a => a.Audits.find(b => !b.IsPrimaryConcentration));
        //Chosen concentration types (Tracks for MAC, Career and Enrichment for MBA) - we need to group our audits by these values
        var concTypes = concData.concentrations.map(c => c.Name);
        //If we have less types than auditCollections, we need to assign a generic 'Group' label for these
        auditCollections.forEach(function (item, index) {            
            if (concTypes.length < index + 1) {
                concTypes.push('Group');
            }
        })        
        //Go through the concentration types and start grouping the audits for display in the widget
        for (let i = 0; i < concTypes.length; i++) {
            var audits = auditCollections[i];
            if (audits.length > 0) {                
                var topAudits = auditCollections[i].slice(0, topN);
                
                var topGrouping = this.createGrouping(concTypes[i], topAudits, chosenConcs);

                var allGrouping = this.createGrouping(concTypes[i], audits, chosenConcs);

                var chosenGrouping = this.createGrouping(concTypes[i], chosenConcAudits, chosenConcs)

                groupedCollections.push({topGrouping, allGrouping, chosenGrouping});  
            }
        }
        return groupedCollections;
    }

    private createGrouping(type, audits, chosenConcs): any {        
        return {
            Type: type,
            Concentrations: audits.map(g => {
                return {
                    Key: g.Key,
                    Audits: g.Audits,
                    IsMyConcentration: chosenConcs.includes(g.Key)
                }
            })
        }
    }

    //public degreePercentageComplete$: Observable<any> = combineLatest(this.degreeDetail$, this.optionalByType$)
    //    .pipe(
    //        switchMap(([degree, concentrations]) => {                
    //            var percentComplete = [];
    //            var retVal = {
    //                DegreeName: '',
    //                PercentageComplete: 0,
    //                PercentageCompleteDescription: ''
    //            }
    //            if (degree && degree.DegreeName != "") {
    //                percentComplete.push(degree.PercentageComplete);
    //                retVal.DegreeName = degree.DegreeName;
    //                for (var i = 0; i < concentrations.length; i++) {
    //                    var myConcentration = concentrations[i].Concentrations.find(a => a.IsMyConcentration);
    //                    if (myConcentration && myConcentration.Audits.length > 0) {
    //                        percentComplete.push(myConcentration.Audits[0].TotalPercentageComplete);
    //                    }
    //                }
    //                if (percentComplete.length > 0) {
    //                    var myPercentageComplete = Math.round(percentComplete.reduce((a, b) => (a + b)) / percentComplete.length);

    //                    retVal.PercentageComplete = myPercentageComplete;

    //                    retVal.PercentageCompleteDescription = `${myPercentageComplete}% Complete`;
    //                }
    //            }
    //            return of(retVal)
    //        })
    //);


    private generatePdf() {
        //ga - log activity
        this.gtm.gaDownload('DegreeAudit');
        this.isLoadingPdf = true;        
        var rootFolder = window.location.pathname.toLowerCase().indexOf('client') > -1 ? '/client' : '';
        var logo = window.location.protocol + '//' + window.location.host + rootFolder + '/assets/images/KenanFlagler_rgb_v.png';
        const url = `${this.degreeAuditApiUrl}/self/pdf?imageUrl=${logo}`;
        return this.http.get(url, { responseType: 'blob' }).pipe(
            map(
                (res: Blob) => {
                    return new Blob([res], { type: 'application/pdf' });
                })
        );
    }

    public openPdf(filename) {
        this.generatePdf().subscribe(
            res => {
                var url = window.URL.createObjectURL(res);

                //  Blobs do not have file names.  This allows for an automatic download of the file while
                //  retaining the original file name.
                this.utility.downloadFile("degree-audit.pdf", url)
                this.isLoadingPdf = false;
            }
        )
    }
}
